<template>
	<div class="schedule-add-edit-form">
		<div class="schedule-add-edit-form__group">
			<DatePicker
				:labelText="'When will the move happen?'"
				datePickerType="singleDate"
				:required="true"
				v-model="parseDate"
				:helpText="'Move scheduled at 6:30 AM EST of that day'"
				:startDate="parseDate.startDate"
				:disableDaysBeforeStartDate="true"
				@hasError="
					val => {
						this.$emit('hasDateError', val);
					}
				"
			/>
			<DropdownV2
				label="Where will the clients move to?"
				v-model="dataSchedule.moveToZoneId"
				:options="ddsEnvironments"
				:placeholder="'Select One'"
				ref="setMoveToZoneIdRef"
				:showClearSelectedButton="true"
				:required="true"
				@change="setMoveToZoneId"
				:errorText="environmentErrorMessage"
			/>
			<DropdownV2
				label="Is this a Sprint Release or HotFix?"
				v-model="dataSchedule.releaseType"
				:options="ddsReleaseType"
				placeholder="Select One"
				ref="setReleaseTypeRef"
				:showClearSelectedButton="true"
				@change="setReleaseType"
				:required="true"
				:errorText="releaseTypeErrorMessage"
			/>
		</div>

		<Textarea
			label="Can you list the reason for the move?"
			:labelErrorMessage="'Comment'"
			:className="'textarea__input'"
			:maxLength="200"
			:required="true"
			:rows="4"
			:showAlwaysCharacterCounter="true"
			v-model="dataSchedule.comment"
			:showErrorMessage="true"
			:forceCustomErrorMessage="commentErrorMessage"
			@input="setComment"
		/>
	</div>
</template>

<script>
import DatePicker from '@/components/forms/date-picker/date-picker';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import Textarea from '@/components/forms/textarea/textarea.vue';
import { required } from 'vuelidate/lib/validators';
import {
	MODULE_NAME as MODULE_OPTION_NAME,
	SET_ENVIRONMENTS,
	ENVIRONMENTS
} from '@/sections/options/options-mutation-type.js';

import { formatDate } from '@/utils/datetime';
import { deepEqual } from '@/utils/object';
import _ from 'lodash';
//vuex
import { mapGetters, mapActions } from 'vuex';

export default {
	name: 'ScheduleAddEditForm',
	components: {
		DatePicker,
		DropdownV2,
		Textarea
	},
	model: {
		prop: 'schedule',
		event: 'update'
	},
	props: {
		schedule: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			dataSchedule: {},
			ddsEnvironments: [],
			ddsReleaseType: [
				{ text: 'Release', value: 'R' },
				{ text: 'Hotfix', value: 'H' }
			]
		};
	},
	validations: {
		dataSchedule: {
			moveToZoneId: {
				required
			},
			releaseType: {
				required
			},
			comment: {
				required
			}
		}
	},
	mounted() {
		this.getApiEnvironments();
	},
	computed: {
		...mapGetters('customers', ['customerById']),
		...mapGetters(MODULE_OPTION_NAME, [ENVIRONMENTS]),

		environmentErrorMessage() {
			if (this.$v.dataSchedule.moveToZoneId.$dirty) {
				if (!this.$v.dataSchedule.moveToZoneId.required) {
					return 'Move to zone is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		releaseTypeErrorMessage() {
			if (this.$v.dataSchedule.releaseType.$dirty) {
				if (!this.$v.dataSchedule.releaseType.required) {
					return 'Release Type is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		commentErrorMessage() {
			if (this.$v.dataSchedule.comment.$dirty) {
				if (!this.$v.dataSchedule.comment.required) {
					return 'Comment is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		parseDate: {
			get() {
				if (this.dataSchedule.scheduledDate) {
					const scheduledDate = new Date(
						this.dataSchedule.scheduledDate
					);
					return scheduledDate <= new Date()
						? new Date(new Date().setDate(new Date().getDate() + 1))
						: scheduledDate;
				} else {
					let today = new Date();
					return new Date(today.setDate(today.getDate() + 1));
				}
			},
			set(value) {
				if (value) {
					this.dataSchedule.scheduledDate = formatDate(value);
				} else {
					this.dataSchedule.scheduledDate = new Date();
				}
			}
		},
		scheduleHasChange() {
			return !deepEqual(this.schedule, this.dataSchedule, true, [
				'customers',
				'removedCustomers'
			]);
		}
	},
	methods: {
		...mapActions(MODULE_OPTION_NAME, {
			getEnvironments: SET_ENVIRONMENTS
		}),
		async getApiEnvironments() {
			if (_.isEmpty(this[ENVIRONMENTS])) {
				await this.getEnvironments({ active: true }).then(response => {
					this.ddsEnvironments = response.items.map(x => {
						return {
							text: x.description,
							value: parseInt(x.value)
						};
					});
				});
			} else {
				this.ddsEnvironments = this[ENVIRONMENTS].map(x => {
					return {
						text: x.description,
						value: parseInt(x.value)
					};
				});
			}
			this.ddsEnvironments.sort((a, b) => {
				return a.value - b.value;
			});
		},
		hasError() {
			this.$emit('hasError', this.$v.$invalid);
		},
		setMoveToZoneId(value) {
			this.dataSchedule.moveToZoneId = value;
			this.$v.dataSchedule.moveToZoneId.$touch();
		},
		setReleaseType(value) {
			this.dataSchedule.releaseType = value;
			this.$v.dataSchedule.releaseType.$touch();
		},
		setComment(value) {
			this.dataSchedule.comment = value;
			this.$v.dataSchedule.comment.$touch();
		},
		emitValue() {
			if (this.scheduleHasChange) {
				const newSchedule = _.omit(this.dataSchedule, [
					'customers',
					'removedCustomers'
				]);
				const {
					customers: updatedCustomers,
					removedCustomers: updatedRemovedCustomers
				} = this.schedule;

				const obj = Object.assign(newSchedule, {
					customers: updatedCustomers,
					removedCustomers: updatedRemovedCustomers
				});

				this.$emit('update', _.cloneDeep(obj));
			}
		}
	},
	watch: {
		schedule: {
			deep: true,
			immediate: true,
			handler(obj) {
				if (!_.isEmpty(obj) && this.scheduleHasChange) {
					this.dataSchedule = _.cloneDeep(obj);
				}
			}
		},
		dataSchedule: {
			deep: true,
			handler(obj) {
				if (!_.isEmpty(obj)) {
					this.emitValue();
				}
				this.hasError();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.schedule-add-edit-form,
.schedule-add-edit-form__group {
	display: flex;
	flex-direction: column;
	gap: $gutter-lg;
}
</style>
